import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export interface HeroTextProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * HeroText - Large, attention-grabbing text for hero sections and landing pages
 * 
 * Use cases:
 * - Main hero headings on landing pages
 * - Primary marketing messages
 * - Large feature announcements
 * 
 * @example
 * ```tsx
 * <HeroText>Blast through writer's block</HeroText>
 * ```
 */
export const HeroText: React.FC<HeroTextProps> = ({ 
  children, 
  sx = {},
  ...props 
}) => {
  return (
    <Typography
      component="h1"
      sx={{
        fontFamily: typographyVariants.hero.fontFamily,
        fontWeight: typographyVariants.hero.fontWeight,
        lineHeight: typographyVariants.hero.lineHeight,
        color: colors.text.primary,
        fontSize: {
          xs: typographyVariants.hero.fontSize.mobile,
          md: typographyVariants.hero.fontSize.tablet,
          lg: typographyVariants.hero.fontSize.desktop,
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
